import { ProcessedData } from "./types";

// Helper function to extract data array from various response formats
export const extractDataArray = (response: any): any[] => {
    // If we already have an array, return it
    if (Array.isArray(response)) {
        return response;
    }

    // Handle the specific format with nested data.data array
    if (response?.data?.data && Array.isArray(response.data.data)) {
        return response.data.data;
    }

    // Handle the previous format where data was directly in response.data
    if (response?.data && typeof response.data === "object") {
        // If data is an array
        if (Array.isArray(response.data)) {
            return response.data;
        }

        // If data is an object with numeric keys
        if (Object.keys(response.data).every((key) => !isNaN(Number(key)))) {
            return Object.values(response.data);
        }
    }

    console.warn("Unsupported data format:", response);
    return [];
};

// Extract metadata from the response
export const extractMetadata = (response: any) => {
    if (response?.data?.dataPoints !== undefined) {
        return {
            dataPoints: response.data.dataPoints,
            originalPoints: response.data.originalPoints,
            timeRange: response.data.timeRange,
            aggregation: response.data.aggregation,
            limitReached: response.data.limitReached,
        };
    }
    return null;
};

// Process the data into a format suitable for charting
export const processData = (data: any): ProcessedData => {
    const dataArray = extractDataArray(data);
    let processed: ProcessedData = {};

    if (!dataArray || dataArray.length === 0) {
        console.log("No data available to process after extraction");
        return processed;
    }

    // Check if we have aggregated data with min/max
    const hasAggregatedData =
        dataArray.length > 0 && dataArray[0].min !== undefined && dataArray[0].max !== undefined;

    dataArray.forEach((item) => {
        const { key, value, timestamp, min, max, count } = item;

        if (!key || value === undefined || timestamp === undefined) {
            return;
        }

        if (!processed[key]) {
            processed[key] = {
                timestamps: [],
                values: [],
                // If we have aggregated data, include min/max arrays
                ...(hasAggregatedData
                    ? {
                          mins: [],
                          maxes: [],
                          counts: [],
                      }
                    : {}),
            };
        }

        processed[key].timestamps.push(new Date(Number(timestamp)));
        processed[key].values.push(parseFloat(value));

        // Add min/max/count if available in the data
        if (hasAggregatedData) {
            processed[key].mins?.push(parseFloat(min));
            processed[key].maxes?.push(parseFloat(max));
            processed[key].counts?.push(count || 1);
        }
    });

    return processed;
};

// Calculate statistics for a series of values
export const calculateStats = (values: number[]) => {
    if (!values || values.length === 0) return { min: 0, max: 0, avg: 0, current: 0 };

    const min = Math.min(...values);
    const max = Math.max(...values);
    const sum = values.reduce((acc, val) => acc + val, 0);
    const avg = sum / values.length;
    const current = values[values.length - 1];

    return {
        min: parseFloat(min.toFixed(2)),
        max: parseFloat(max.toFixed(2)),
        avg: parseFloat(avg.toFixed(2)),
        current: parseFloat(current.toFixed(2)),
    };
};

// Detect anomalies in data
export const detectAnomalies = (values: number[]) => {
    if (!values || values.length < 5) return [];

    const stats = calculateStats(values);
    const stdDev = Math.sqrt(
        values.reduce((sum, val) => sum + Math.pow(val - stats.avg, 2), 0) / values.length
    );

    const threshold = 2.5; // Number of standard deviations to consider an anomaly

    return values
        .map((val, index) => {
            const zScore = Math.abs((val - stats.avg) / stdDev);
            return zScore > threshold ? index : null;
        })
        .filter((index) => index !== null) as number[];
};
